import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { formatCurrency, formatDate, getMonthName, getCurrentYear, toISODateString } from '../lib/utils';
import { Plus, Pencil, Trash2, CalendarIcon, Receipt, TrendingDown, Filter, X, CheckSquare } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { ExportButtons } from '../components/ExportButtons';
import { SnapshotButtons } from '../components/SnapshotButtons';

export function DepensesPage() {
  const { api } = useAuth();
  const [prevData, setPrevData] = useState([]);
  const [reelData, setReelData] = useState([]);
  const [refDepenses, setRefDepenses] = useState([]);
  const [year, setYear] = useState(getCurrentYear());
  const [month, setMonth] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [filterCat, setFilterCat] = useState('all');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [selectedPrev, setSelectedPrev] = useState(new Set());
  const [selectedReel, setSelectedReel] = useState(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleteTarget, setBulkDeleteTarget] = useState(null); // 'prev' or 'reel'

  useEffect(() => {
    fetchData();
    fetchRef();
  }, [year, month]);

  // Clear selections when data changes
  useEffect(() => { setSelectedPrev(new Set()); }, [prevData]);
  useEffect(() => { setSelectedReel(new Set()); }, [reelData]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { annee: year };
      if (month) params.mois = month;
      const [prevRes, reelRes] = await Promise.all([
        api.get('/depenses-prev', { params }),
        api.get('/depenses-reelles', { params })
      ]);
      setPrevData(prevRes.data);
      setReelData(reelRes.data);
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setLoading(false);
    }
  };

  const fetchRef = async () => {
    try {
      const res = await api.get('/ref-depenses');
      setRefDepenses(res.data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/depenses-prev/${id}`);
      toast.success('Supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDeleteReel = async (id) => {
    try {
      await api.delete(`/depenses-reelles/${id}`);
      toast.success('Dépense réelle supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleBulkDelete = async () => {
    try {
      if (bulkDeleteTarget === 'prev') {
        await api.post('/depenses-prev/delete-bulk', { ids: [...selectedPrev] });
        toast.success(`${selectedPrev.size} dépense(s) prévisionnelle(s) supprimée(s)`);
        setSelectedPrev(new Set());
      } else {
        await api.post('/depenses-reelles/delete-bulk', { ids: [...selectedReel] });
        toast.success(`${selectedReel.size} dépense(s) réelle(s) supprimée(s)`);
        setSelectedReel(new Set());
      }
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
    setBulkDeleteOpen(false);
    setBulkDeleteTarget(null);
  };

  const togglePrevSelect = (id) => {
    setSelectedPrev(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleReelSelect = (id) => {
    setSelectedReel(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const dateFilteredPrev = prevData.filter(d => {
    if (dateFrom && d.date < dateFrom) return false;
    if (dateTo && d.date > dateTo) return false;
    return true;
  });
  const dateFilteredReel = reelData.filter(d => {
    if (dateFrom && d.date < dateFrom) return false;
    if (dateTo && d.date > dateTo) return false;
    return true;
  });
  const totalPrev = dateFilteredPrev.reduce((sum, d) => sum + d.montant_prevu, 0);
  const totalReel = dateFilteredReel.reduce((sum, d) => sum + d.montant_absolu, 0);
  const filteredPrev = filterCat === 'all' ? dateFilteredPrev : dateFilteredPrev.filter(d => (d.categorie_interne || 'Non catégorisé') === filterCat);
  const filteredPrevTotal = filteredPrev.reduce((sum, d) => sum + d.montant_prevu, 0);

  const catStats = {};
  dateFilteredReel.forEach(d => {
    const cat = d.categorie_interne || 'A Catégoriser';
    if (!catStats[cat]) catStats[cat] = { count: 0, total: 0, tva: 0 };
    catStats[cat].count += 1;
    catStats[cat].total += d.montant_absolu || 0;
    catStats[cat].tva += d.tva_deductible || 0;
  });
  dateFilteredPrev.forEach(d => {
    const cat = d.categorie_interne || 'Non catégorisé';
    if (!catStats[cat]) catStats[cat] = { count: 0, total: 0, tva: 0 };
  });
  const categories = Object.keys(catStats).sort();
  const filteredReel = filterCat === 'all' ? dateFilteredReel : dateFilteredReel.filter(d => (d.categorie_interne || 'A Catégoriser') === filterCat);
  const filteredTotal = filteredReel.reduce((s, d) => s + (d.montant_absolu || 0), 0);
  const filteredTVA = filteredReel.reduce((s, d) => s + (d.tva_deductible || 0), 0);

  const allPrevIds = filteredPrev.map(d => d.id);
  const allPrevSelected = allPrevIds.length > 0 && allPrevIds.every(id => selectedPrev.has(id));
  const somePrevSelected = allPrevIds.some(id => selectedPrev.has(id));

  const allReelIds = filteredReel.map(d => d.id);
  const allReelSelected = allReelIds.length > 0 && allReelIds.every(id => selectedReel.has(id));
  const someReelSelected = allReelIds.some(id => selectedReel.has(id));

  const toggleAllPrev = () => {
    if (allPrevSelected) {
      setSelectedPrev(new Set());
    } else {
      setSelectedPrev(new Set(allPrevIds));
    }
  };

  const toggleAllReel = () => {
    if (allReelSelected) {
      setSelectedReel(new Set());
    } else {
      setSelectedReel(new Set(allReelIds));
    }
  };

  const DepenseForm = ({ item, onClose }) => {
    const [form, setForm] = useState(item ? {
      date: item.date,
      libelle: item.libelle,
      montant_prevu: item.montant_prevu
    } : {
      date: toISODateString(new Date()),
      libelle: '',
      montant_prevu: 0
    });
    const [dateOpen, setDateOpen] = useState(false);

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        if (item?.id) {
          await api.put(`/depenses-prev/${item.id}`, form);
          toast.success('Modifié');
        } else {
          await api.post('/depenses-prev', form);
          toast.success('Créé');
        }
        fetchData();
        onClose();
      } catch (error) {
        toast.error('Erreur');
      }
    };

    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Date</Label>
            <Popover open={dateOpen} onOpenChange={setDateOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start text-left font-normal">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {form.date ? format(new Date(form.date), 'PPP', { locale: fr }) : 'Choisir'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={form.date ? new Date(form.date) : undefined}
                  onSelect={(date) => {
                    setForm({ ...form, date: toISODateString(date) });
                    setDateOpen(false);
                  }}
                  locale={fr}
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-2">
            <Label>Montant prévu (€)</Label>
            <Input
              type="number"
              step="0.01"
              value={form.montant_prevu}
              onChange={(e) => setForm({ ...form, montant_prevu: parseFloat(e.target.value) || 0 })}
            />
          </div>

          <div className="col-span-2 space-y-2">
            <Label>Libellé</Label>
            <Select value={form.libelle} onValueChange={(v) => setForm({ ...form, libelle: v })}>
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner un libellé" />
              </SelectTrigger>
              <SelectContent>
                {refDepenses.map(r => (
                  <SelectItem key={r.id} value={r.libelle}>{r.libelle} ({r.categorie})</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Le libellé doit correspondre au référentiel dépenses pour auto-remplir catégorie et TVA
            </p>
          </div>
        </div>

        <DialogFooter>
          <DialogClose asChild><Button type="button" variant="outline">Annuler</Button></DialogClose>
          <Button type="submit">{item?.id ? 'Modifier' : 'Créer'}</Button>
        </DialogFooter>
      </form>
    );
  };

  // Selection action bar component
  const SelectionBar = ({ count, onDelete, onClear, target }) => (
    <div className="flex items-center gap-3 px-4 py-2.5 bg-primary/5 border border-primary/20 rounded-lg animate-in slide-in-from-top-2 duration-200">
      <CheckSquare className="w-4 h-4 text-primary" />
      <span className="text-sm font-medium">{count} sélectionné(s)</span>
      <div className="flex items-center gap-2 ml-auto">
        <Button variant="ghost" size="sm" onClick={onClear} className="gap-1.5 h-8" data-testid={`deselect-all-${target}`}>
          <X className="w-3.5 h-3.5" /> Désélectionner
        </Button>
        <Button
          variant="destructive"
          size="sm"
          className="gap-1.5 h-8"
          onClick={() => { setBulkDeleteTarget(target); setBulkDeleteOpen(true); }}
          data-testid={`bulk-delete-${target}`}
        >
          <Trash2 className="w-3.5 h-3.5" /> Supprimer ({count})
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="module-header">
        <div>
          <h1 className="module-title">Dépenses</h1>
          <p className="text-muted-foreground mt-1">Prévisions et dépenses réelles (issues de Linxo)</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <SnapshotButtons module="depenses" moduleLabel="Dépenses" />
          <ExportButtons api={api} moduleKey="depenses" year={year} month={month} />
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
          <SelectTrigger className="w-[120px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[2024, 2025, 2026, 2027, 2028, 2029, 2030].map(y => (
              <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={month?.toString() || 'all'} onValueChange={(v) => setMonth(v === 'all' ? null : parseInt(v))}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Tous les mois" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les mois</SelectItem>
            {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
              <SelectItem key={m} value={m.toString()}>{getMonthName(m)}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={filterCat} onValueChange={setFilterCat}>
          <SelectTrigger className="w-[200px]" data-testid="filter-categorie">
            <SelectValue placeholder="Catégorie" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les catégories</SelectItem>
            {categories.map(cat => (
              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <Label className="text-xs whitespace-nowrap">Du</Label>
          <Input type="date" className="w-[150px] h-9" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="filter-date-from" />
          <Label className="text-xs whitespace-nowrap">Au</Label>
          <Input type="date" className="w-[150px] h-9" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="filter-date-to" />
          {(dateFrom || dateTo || filterCat !== 'all') && <Button variant="ghost" size="sm" onClick={() => { setDateFrom(''); setDateTo(''); setFilterCat('all'); }}>Effacer</Button>}
        </div>

        <div className="ml-auto flex items-center gap-4">
          <div className="px-3 py-2 bg-muted rounded-full text-sm">
            Prévu: <span className="font-medium tabular-nums">{formatCurrency(filteredPrevTotal)}</span>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-red-100 dark:bg-red-900/20 rounded-full">
            <TrendingDown className="w-4 h-4 text-red-600" />
            <span className="text-sm font-medium">Réel:</span>
            <span className="text-lg font-bold text-red-600 tabular-nums">{formatCurrency(filteredTotal)}</span>
          </div>
        </div>
      </div>

      {/* Bulk delete confirmation dialog */}
      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer la sélection ?</AlertDialogTitle>
            <AlertDialogDescription>
              {bulkDeleteTarget === 'prev'
                ? `${selectedPrev.size} dépense(s) prévisionnelle(s) seront supprimées. Cette action est irréversible.`
                : `${selectedReel.size} dépense(s) réelle(s) seront supprimées. Cette action est irréversible.`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkDelete} data-testid="confirm-bulk-delete">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Tabs defaultValue="prevues">
        <TabsList>
          <TabsTrigger value="prevues" className="gap-2">
            <Receipt className="w-4 h-4" /> Prévisionnelles ({filteredPrev.length})
          </TabsTrigger>
          <TabsTrigger value="reelles" className="gap-2">
            <TrendingDown className="w-4 h-4" /> Réelles ({dateFilteredReel.length})
          </TabsTrigger>
        </TabsList>

        {/* Prévisionnelles */}
        <TabsContent value="prevues" className="space-y-3">
          {selectedPrev.size > 0 && (
            <SelectionBar count={selectedPrev.size} onClear={() => setSelectedPrev(new Set())} target="prev" />
          )}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Dépenses Prévisionnelles</CardTitle>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="gap-2" onClick={() => setEditItem(null)}>
                    <Plus className="w-4 h-4" /> Nouvelle dépense
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{editItem ? 'Modifier' : 'Nouvelle'} Dépense</DialogTitle>
                  </DialogHeader>
                  <DepenseForm item={editItem} onClose={() => setDialogOpen(false)} />
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[40px] pl-4">
                      <Checkbox
                        checked={allPrevSelected}
                        ref={(el) => { if (el) el.dataset.indeterminate = somePrevSelected && !allPrevSelected; }}
                        onCheckedChange={toggleAllPrev}
                        data-testid="select-all-prev"
                        aria-label="Tout sélectionner"
                      />
                    </TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Libellé</TableHead>
                    <TableHead>Catégorie</TableHead>
                    <TableHead className="text-right">TVA</TableHead>
                    <TableHead className="text-right">Montant prévu</TableHead>
                    <TableHead className="w-[100px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPrev.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        Aucune dépense prévisionnelle
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredPrev.map(item => (
                      <TableRow key={item.id} className={selectedPrev.has(item.id) ? 'bg-primary/5' : ''}>
                        <TableCell className="pl-4">
                          <Checkbox
                            checked={selectedPrev.has(item.id)}
                            onCheckedChange={() => togglePrevSelect(item.id)}
                            data-testid={`select-prev-${item.id}`}
                            aria-label={`Sélectionner ${item.libelle}`}
                          />
                        </TableCell>
                        <TableCell>{formatDate(item.date)}</TableCell>
                        <TableCell className="font-medium">{item.libelle}</TableCell>
                        <TableCell className="text-muted-foreground">{item.categorie_interne || '-'}</TableCell>
                        <TableCell className="text-right tabular-nums">{item.tva ? `${(item.tva * 100).toFixed(0)}%` : '-'}</TableCell>
                        <TableCell className="text-right tabular-nums font-medium">{formatCurrency(item.montant_prevu)}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" onClick={() => { setEditItem(item); setDialogOpen(true); }}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="icon" className="text-destructive"><Trash2 className="w-4 h-4" /></Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Supprimer ?</AlertDialogTitle>
                                  <AlertDialogDescription>Cette action est irréversible.</AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Annuler</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => handleDelete(item.id)}>Supprimer</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Réelles */}
        <TabsContent value="reelles" className="space-y-4">
          {/* Résumé par catégorie */}
          {categories.length > 0 && (
            <div className="flex flex-wrap gap-2">
              <Button
                variant={filterCat === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterCat('all')}
                data-testid="filter-cat-all"
              >
                Toutes ({reelData.length})
              </Button>
              {categories.map(cat => (
                <Button
                  key={cat}
                  variant={filterCat === cat ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterCat(cat)}
                  data-testid={`filter-cat-${cat}`}
                  className="gap-1"
                >
                  {cat}
                  <Badge variant="secondary" className="ml-1 text-xs">{catStats[cat].count}</Badge>
                  <span className="text-xs opacity-70">{formatCurrency(catStats[cat].total)}</span>
                </Button>
              ))}
            </div>
          )}

          {/* Totaux filtrés */}
          {filterCat !== 'all' && (
            <div className="flex items-center gap-4 p-3 bg-muted/50 rounded-lg">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium">{filterCat}</span>
              <span className="text-sm">{filteredReel.length} lignes</span>
              <span className="text-sm font-bold tabular-nums text-red-600">{formatCurrency(filteredTotal)}</span>
              <span className="text-xs text-muted-foreground">TVA déd: {formatCurrency(filteredTVA)}</span>
              <Button variant="ghost" size="sm" className="ml-auto" onClick={() => setFilterCat('all')}>Effacer filtre</Button>
            </div>
          )}

          {selectedReel.size > 0 && (
            <SelectionBar count={selectedReel.size} onClear={() => setSelectedReel(new Set())} target="reel" />
          )}

          <Card>
            <CardHeader>
              <CardTitle>
                Dépenses Réelles
                {filterCat !== 'all' && <span className="text-base font-normal text-muted-foreground ml-2">— {filterCat}</span>}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[40px] pl-4">
                      <Checkbox
                        checked={allReelSelected}
                        ref={(el) => { if (el) el.dataset.indeterminate = someReelSelected && !allReelSelected; }}
                        onCheckedChange={toggleAllReel}
                        data-testid="select-all-reel"
                        aria-label="Tout sélectionner"
                      />
                    </TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Libellé</TableHead>
                    <TableHead>Cat. Linxo</TableHead>
                    <TableHead>Cat. Interne</TableHead>
                    <TableHead>Sous-cat.</TableHead>
                    <TableHead className="text-right">Montant</TableHead>
                    <TableHead className="text-right">TVA déductible</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredReel.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                        {reelData.length === 0 ? 'Aucune dépense réelle - Importez des données Linxo' : 'Aucune dépense pour cette catégorie'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredReel.map(item => (
                      <TableRow key={item.id} className={selectedReel.has(item.id) ? 'bg-primary/5' : ''}>
                        <TableCell className="pl-4">
                          <Checkbox
                            checked={selectedReel.has(item.id)}
                            onCheckedChange={() => toggleReelSelect(item.id)}
                            data-testid={`select-reel-${item.id}`}
                            aria-label={`Sélectionner ${item.libelle}`}
                          />
                        </TableCell>
                        <TableCell>{formatDate(item.date)}</TableCell>
                        <TableCell className="font-medium max-w-[200px] truncate">{item.libelle}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">{item.categorie_linxo}</TableCell>
                        <TableCell>
                          <Badge variant={item.categorie_interne === 'A Catégoriser' ? 'outline' : 'secondary'} className="text-xs">
                            {item.categorie_interne}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">{item.sous_categorie || '-'}</TableCell>
                        <TableCell className="text-right tabular-nums font-medium text-red-600">
                          {formatCurrency(item.montant_absolu)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{formatCurrency(item.tva_deductible)}</TableCell>
                        <TableCell>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" data-testid={`del-reel-${item.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader><AlertDialogTitle>Supprimer cette dépense ?</AlertDialogTitle><AlertDialogDescription>{item.libelle} — {formatCurrency(item.montant_absolu)}</AlertDialogDescription></AlertDialogHeader>
                              <AlertDialogFooter><AlertDialogCancel>Annuler</AlertDialogCancel><AlertDialogAction onClick={() => handleDeleteReel(item.id)}>Supprimer</AlertDialogAction></AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                  {filteredReel.length > 0 && (
                    <TableRow className="bg-muted/50 font-bold">
                      <TableCell></TableCell>
                      <TableCell colSpan={5}>Total{filterCat !== 'all' ? ` ${filterCat}` : ''}</TableCell>
                      <TableCell className="text-right tabular-nums text-red-700">{formatCurrency(filteredTotal)}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatCurrency(filteredTVA)}</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
